/**
 * useFilters - Hook for managing filter state
 */

import { useState, useCallback, useMemo } from 'react';
import type { FilterCondition, FilterLogic, ColumnDefinition, FilterOperator, RelativeDate } from '../columns/types';
import { DEFAULT_OPERATOR_BY_TYPE } from './constants';

// Generate unique IDs for filters
let filterId = 0;
function generateFilterId(): string {
  return `filter-${++filterId}-${Date.now()}`;
}

export interface UseFiltersOptions {
  initialFilters?: FilterCondition[];
  initialLogic?: FilterLogic;
}

export interface UseFiltersReturn {
  filters: FilterCondition[];
  filterLogic: FilterLogic;
  addFilter: (column: ColumnDefinition) => void;
  updateFilter: (id: string, updates: Partial<Omit<FilterCondition, 'id'>>) => void;
  removeFilter: (id: string) => void;
  clearFilters: () => void;
  setFilterLogic: (logic: FilterLogic) => void;
  setFilters: (filters: FilterCondition[]) => void;
  hasActiveFilters: boolean;
  // Convert filters to API format (strip id field)
  toApiFilters: () => Array<Omit<FilterCondition, 'id'>>;
}

export function useFilters(options: UseFiltersOptions = {}): UseFiltersReturn {
  const [filters, setFiltersState] = useState<FilterCondition[]>(
    options.initialFilters?.map(f => ({ ...f, id: f.id || generateFilterId() })) || []
  );
  const [filterLogic, setFilterLogic] = useState<FilterLogic>(
    options.initialLogic || 'and'
  );

  // Add a new filter for a column
  const addFilter = useCallback((column: ColumnDefinition) => {
    const newFilter: FilterCondition = {
      id: generateFilterId(),
      column: column.key,
      operator: column.filterOperators?.[0] || DEFAULT_OPERATOR_BY_TYPE[column.type],
      value: getDefaultValue(column),
    };

    setFiltersState(prev => [...prev, newFilter]);
  }, []);

  // Update an existing filter
  const updateFilter = useCallback((id: string, updates: Partial<Omit<FilterCondition, 'id'>>) => {
    setFiltersState(prev =>
      prev.map(filter =>
        filter.id === id ? { ...filter, ...updates } : filter
      )
    );
  }, []);

  // Remove a filter
  const removeFilter = useCallback((id: string) => {
    setFiltersState(prev => prev.filter(f => f.id !== id));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFiltersState([]);
  }, []);

  // Set all filters at once (for loading saved views)
  const setFilters = useCallback((newFilters: FilterCondition[]) => {
    setFiltersState(newFilters.map(f => ({ ...f, id: f.id || generateFilterId() })));
  }, []);

  // Convert to API format (strip id, keep only necessary fields)
  const toApiFilters = useCallback((): Array<Omit<FilterCondition, 'id'>> => {
    return filters.map(({ id: _id, ...rest }) => rest);
  }, [filters]);

  // Check if there are any active filters
  const hasActiveFilters = useMemo(() => filters.length > 0, [filters]);

  return {
    filters,
    filterLogic,
    addFilter,
    updateFilter,
    removeFilter,
    clearFilters,
    setFilterLogic,
    setFilters,
    hasActiveFilters,
    toApiFilters,
  };
}

// Get default value based on column type
function getDefaultValue(column: ColumnDefinition): unknown {
  switch (column.type) {
    case 'boolean':
      return true;
    case 'number':
    case 'currency':
      return '';
    case 'select':
      return column.options?.[0]?.value || '';
    case 'multiselect':
      return [];
    case 'date':
      return ''; // Will use relativeTo for dates
    default:
      return '';
  }
}

// Helper to create a filter condition from a saved view
export function createFilterFromView(
  filter: { column: string; operator: FilterOperator; value: unknown; relativeTo?: RelativeDate }
): FilterCondition {
  return {
    id: generateFilterId(),
    column: filter.column,
    operator: filter.operator,
    value: filter.value,
    relativeTo: filter.relativeTo,
  };
}
